import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Alert,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { visitorService } from '../../../../shared/services/visitors/visitorService';
import { useToast } from '../../../../shared/hooks/useToast';

interface PaymentConfirmationProps {
  participantId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function PaymentConfirmation({
  participantId,
  isOpen,
  onClose,
  onSuccess,
}: PaymentConfirmationProps) {
  const queryClient = useQueryClient();
     const { showToast } = useToast();
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'pix' | 'cash'>('cash');
  

  const mutation = useMutation({
    mutationFn: () => visitorService.confirmPayment(participantId, paymentMethod),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['participant', participantId] });
      showToast('Pagamento registrado!', 'success');
      onSuccess?.();
      onClose();
    },
  });

  const handleConfirm = () => {
    mutation.mutate();
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={600}>
          Confirmar Pagamento
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Selecione o método de pagamento utilizado:
        </Typography>
        
        <FormControl component="fieldset">
          <RadioGroup
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value as 'card' | 'pix' | 'cash')}
          >
            <FormControlLabel
              value="cash"
              control={<Radio />}
              label="Dinheiro"
            />
            <FormControlLabel
              value="pix"
              control={<Radio />}
              label="PIX"
            />
            <FormControlLabel
              value="card"
              control={<Radio />}
              label="Cartão"
            />
          </RadioGroup>
        </FormControl>

        {mutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Erro ao confirmar pagamento. Tente novamente.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Confirmando...' : 'Confirmar Pagamento'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}